import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { ProductGrid } from "@/components/product/ProductGrid";
import { FilterSidebar } from "@/components/shop/FilterSidebar";
import { SortSelect } from "@/components/shop/SortSelect";
import { QuickViewModal } from "@/components/product/QuickViewModal";
import { useFacets, useProducts } from "@/hooks/useCatalog";

const EMPTY = { search: "", category: "all", material: "all", collection: "all", availability: "all", minPrice: undefined, maxPrice: undefined };

export default function ShopPage() {
  const [params] = useSearchParams();
  const [filters, setFilters] = useState(EMPTY);
  const [sort, setSort] = useState("featured");
  const [limit, setLimit] = useState(12);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [quick, setQuick] = useState(null);

  const { data: facets } = useFacets();

  // Sync from URL (search modal / collection card links)
  useEffect(() => {
    setFilters((f) => ({
      ...f,
      search: params.get("search") || "",
      category: params.get("category") || "all",
      collection: params.get("collection") || "all",
    }));
    setLimit(12);
  }, [params]);

  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      category: filters.category,
      material: filters.material,
      collection: filters.collection,
      availability: filters.availability,
      min_price: filters.minPrice,
      max_price: filters.maxPrice,
      sort,
      page: 1,
      limit,
    }),
    [filters, sort, limit]
  );

  const { data, isLoading, isFetching } = useProducts(query);
  const items = data?.items || [];

  const update = (partial) => {
    setFilters((f) => ({ ...f, ...partial }));
    setLimit(12);
  };
  const clearAll = () => {
    setFilters(EMPTY);
    setLimit(12);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <SEO title="Shop All Jewellery" description="Browse the full ÉLORA jewellery collection — filter by category, material, collection and price." />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Shop" }]} />

      <header className="mt-6 border-b border-border pb-8 text-center">
        <h1 className="font-serif text-4xl font-light text-ink sm:text-5xl">The Collection</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
          Handcrafted jewellery for every moment — explore, filter and find the piece that's meant for you.
        </p>
      </header>

      <div className="mt-8 flex gap-10">
        {/* Desktop sidebar */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-28">
            <FilterSidebar facets={facets} filters={filters} onChange={update} onClear={clearAll} />
          </div>
        </aside>

        {/* Main */}
        <div className="min-w-0 flex-1">
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileFilters(true)}
                className="flex items-center gap-2 border border-border px-4 py-2.5 text-xs uppercase tracking-wider text-ink hover:border-gold lg:hidden"
                data-testid="mobile-filters-trigger"
              >
                <SlidersHorizontal size={15} /> Filters
              </button>
              <p className="text-xs uppercase tracking-wider text-muted-foreground" data-testid="results-count">
                {data?.total ?? 0} {data?.total === 1 ? "Piece" : "Pieces"}
              </p>
            </div>
            <SortSelect value={sort} onChange={setSort} />
          </div>

          {filters.search && (
            <div className="mb-5 flex items-center gap-2 text-sm text-ink">
              <span>Results for “{filters.search}”</span>
              <button onClick={() => update({ search: "" })} className="text-muted-foreground hover:text-gold" aria-label="Clear search">
                <X size={14} />
              </button>
            </div>
          )}

          <ProductGrid products={items} loading={isLoading} onQuickView={setQuick} />

          {data?.has_more && (
            <div className="mt-14 text-center">
              <button
                onClick={() => setLimit((l) => l + 12)}
                disabled={isFetching}
                data-testid="load-more-button"
                className="border border-ink px-10 py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-ivory disabled:opacity-60"
              >
                {isFetching ? "Loading…" : "Load More"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile filters sheet */}
      <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
        <SheetContent side="left" className="w-[88%] max-w-sm overflow-y-auto border-r border-border bg-ivory p-6 [&>button]:hidden">
          <SheetTitle className="sr-only">Filters</SheetTitle>
          <SheetDescription className="sr-only">Filter products</SheetDescription>
          <div className="mb-4 flex items-center justify-between">
            <span className="font-serif text-2xl text-ink">Filters</span>
            <button onClick={() => setMobileFilters(false)} aria-label="Close filters" className="text-ink hover:text-gold">
              <X size={20} />
            </button>
          </div>
          <FilterSidebar facets={facets} filters={filters} onChange={update} onClear={clearAll} />
          <button
            onClick={() => setMobileFilters(false)}
            className="mt-6 w-full bg-ink py-3.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ivory"
          >
            Show {data?.total ?? 0} Results
          </button>
        </SheetContent>
      </Sheet>

      <QuickViewModal product={quick} open={!!quick} onOpenChange={(o) => !o && setQuick(null)} />
    </div>
  );
}
